import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/TicketDetail.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TicketDetail({ user }) {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchTicket();
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const token = localStorage.getItem('armesa_token');
      const response = await axios.get(`${API_URL}/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicket(response.data.ticket || response.data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('armesa_token');
      const response = await axios.get(`${API_URL}/api/tickets/${ticketId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const updateTicket = async (updates) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('armesa_token');
      await axios.put(`${API_URL}/api/tickets/${ticketId}/notes`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTicket();
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Fehler beim Aktualisieren des Tickets');
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await updateTicket({ notes: newNote });
    setNewNote('');
  };

  if (loading) {
    return <div className="loading">loading...</div>;
  }

  if (!ticket) {
    return <div className="error">Ticket not found</div>;
  }

  return (
    <div className="ticket-detail-page" data-testid="ticket-detail">
      <div className="detail-header">
        <button onClick={() => navigate('/tickets')} className="btn btn-secondary">
          <i className="fas fa-arrow-left"></i> Back
        </button>
        <h1>Ticket #{ticket.id ? ticket.id.substring(0, 8) : 'N/A'}</h1>
      </div>

      <div className="detail-grid">
        <div className="detail-main">
          <div className="card ticket-info">
            <div className="ticket-info-header">
              <h2>{ticket.subject}</h2>
              <div className="ticket-badges">
                <span className={`badge badge-${ticket.priority || 'medium'}`}>{(ticket.priority || 'medium').toUpperCase()}</span>
                <span className={`badge badge-${ticket.status || 'open'}`}>{(ticket.status || 'open').toUpperCase()}</span>
                {ticket.claimed_by && (
                  <span className="badge badge-info">
                    <i className="fas fa-user"></i> {ticket.claimed_by}
                  </span>
                )}
                {ticket.escalation_flag && <span className="badge badge-critical">ESCALATED</span>}
              </div>
            </div>

            <div className="ticket-description">
              <h3>Description</h3>
              <p>{ticket.description || 'No description available'}</p>
            </div>

            <div className="ticket-metadata">
              <div className="meta-item">
                <i className="fas fa-tag"></i>
                <span>Roblox Username: {ticket.type}</span>
              </div>
              <div className="meta-item">
                <i className="fas fa-globe"></i>
                <span>Language: {(ticket.lang || 'de').toUpperCase()}</span>
              </div>
              <div className="meta-item">
                <i className="fas fa-user"></i>
                <span>Creator: {ticket.username}</span>
              </div>
              <div className="meta-item">
                <i className="fas fa-calendar"></i>
                <span>Created: {ticket.created_at ? new Date(ticket.created_at).toLocaleString('de-DE') : 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className="card live-chat-section">
            <h3>
              <i className="fas fa-comments"></i> Live Chat (Discord)
            </h3>
            
            <div className="chat-messages">
              {messages.length > 0 ? (
                messages.map((msg, idx) => (
                  <div key={idx} className="chat-message">
                    <div className="message-header">
                      <strong className="message-author">{msg.author}</strong>
                      <span className="message-time">
                        {new Date(msg.timestamp).toLocaleString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="message-content">{msg.content}</div>
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="message-attachments">
                        {msg.attachments.map((att, i) => (
                          <a key={i} href={att} target="_blank" rel="noopener noreferrer" className="attachment-link">
                            <i className="fas fa-paperclip"></i> Appendix {i + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-messages">
                  <i className="fas fa-inbox"></i>
                  <p>No messages available</p>
                </div>
              )}
            </div>
          </div>

          <div className="card notes-section">
            <h3>Internal notes</h3>
            
            <div className="notes-content">
              {ticket.notes ? (
                <p className="current-notes">{ticket.notes}</p>
              ) : (
                <p className="no-notes">No notes available</p>
              )}
            </div>

            {user.role !== 'viewer' && (
              <div className="add-note">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Notiz hinzufügen oder aktualisieren..."
                  rows="3"
                  data-testid="note-input"
                />
                <button onClick={addNote} className="btn btn-primary" disabled={updating}>
                  <i className="fas fa-save"></i> Save note
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="detail-sidebar">
          {user.role !== 'viewer' && (
            <div className="card actions-card">
              <h3>Actions</h3>
              
              <div className="action-group">
                <label>Change Status</label>
                <select
                  value={ticket.status || 'open'}
                  onChange={(e) => updateTicket({ status: e.target.value })}
                  disabled={updating}
                  data-testid="status-select"
                >
                  <option value="open">Open</option>
                  <option value="claimed">In progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="action-group">
                <label>Change Priority</label>
                <select
                  value={ticket.priority || 'medium'}
                  onChange={(e) => updateTicket({ priority: e.target.value })}
                  disabled={updating}
                  data-testid="priority-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
          )}

          <div className="card timeline-card">
            <h3>Timeline</h3>
            <div className="timeline">
              <div className="timeline-item">
                <i className="fas fa-plus-circle"></i>
                <div>
                  <strong>Created</strong>
                  <span>{ticket.created_at ? new Date(ticket.created_at).toLocaleString('de-DE') : 'N/A'}</span>
                </div>
              </div>
              {ticket.claimed_at && (
                <div className="timeline-item">
                  <i className="fas fa-hand-paper"></i>
                  <div>
                    <strong>Claimed by {ticket.claimed_by}</strong>
                    <span>{new Date(ticket.claimed_at).toLocaleString('de-DE')}</span>
                  </div>
                </div>
              )}
              {ticket.first_response_at && (
                <div className="timeline-item">
                  <i className="fas fa-reply"></i>
                  <div>
                    <strong>First response</strong>
                    <span>{new Date(ticket.first_response_at).toLocaleString('de-DE')}</span>
                  </div>
                </div>
              )}
              {ticket.closed_at && (
                <div className="timeline-item">
                  <i className="fas fa-check-circle"></i>
                  <div>
                    <strong>Closed by {ticket.closed_by}</strong>
                    <span>{new Date(ticket.closed_at).toLocaleString('de-DE')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketDetail;